import React, { ReactElement, useEffect, useState } from "react";
import {
  KameleonThemeProvider,
  mergeThemes,
  ZilchPresets,
} from "@kameleon-util/global-theme";
import { Script } from "gatsby";
import { ErrorBoundary } from "react-error-boundary";
import { KameleonIconContext } from "@kameleon-common/icons";
import Footer from "./Footer";
import { DevelopmentConnectionError } from "./development/development-connection-error";
import "../styles/main.scss";
import { escapeGraphQLKeywordsIn } from "../../utils/json-utils";
import Header from "./Header";
import { fallBackRender, logError } from "../util-components/error-utils";
import { LayoutPropsWithChildren } from "../types/common/prop-types";

export default function Layout({
  children,
  pageContext,
}: LayoutPropsWithChildren): ReactElement {
  const [webfontLoaded, setWebfontLoaded] = useState(false);

  useEffect(() => {
    // When webfont loads, add a 'loaded' class to body to trigger the opacity transition.
    if (webfontLoaded) {
      document.body.classList.add("loaded");
    }
  }, [webfontLoaded]);

  const { settings, header, footer } = pageContext;
  const preset =
    (ZilchPresets as any)[settings?.profile?.toneOfVoice ?? ""] ?? undefined;
  const theme = escapeGraphQLKeywordsIn(settings?.theme ?? {}, true);

  const content = (
    <>
      <Script src="/webfont.js" onLoad={(): void => setWebfontLoaded(true)} />
      <KameleonThemeProvider theme={mergeThemes(preset?.() ?? {}, theme)}>
        <KameleonIconContext.Provider value={settings?.theme?.fonts?.icons}>
          {webfontLoaded && <Header {...header} />}
          <main className="zilch--layout">{children}</main>
          <Footer {...footer} />
        </KameleonIconContext.Provider>
      </KameleonThemeProvider>
    </>
  );

  return (
    <DevelopmentConnectionError>
      {process.env.NODE_ENV !== "production" ? (
        <ErrorBoundary fallbackRender={fallBackRender} onError={logError}>
          {content}
        </ErrorBoundary>
      ) : (
        content
      )}
    </DevelopmentConnectionError>
  );
}
